import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Button,Container , Grid,  IconButton, InputAdornment, List, ListItem,ListItemText, ListItemIcon, TextField } from '@aura/components'
import { withStyles } from '@aura/components/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Form from '@aura/forms/form'
import { useTranslation } from 'react-i18next'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import PropTypes from 'prop-types'
import RegistrationContainer from '../../../../components/new-registration-container'
import { passwordContainsUsername, hasSequentialChars, hasIdenticalConsecutiveChars } from '../../../../utils/password-validation'
import formFields, { initialValues } from './constants/form-fields'
import styles from './styles'


const Component = ({
    classes,
    data,
    error,
    isProcessing,
    onNext
}) => {
    const ref = useRef()
    const isServerErrorCleared = useRef(false)
    const [ fieldValidity, setFieldValidity ] = useState({
        confirmPassword: false,
        password: false
    })
    const { t: translate, i18n } = useTranslation()
    const [ nextButtonDisabled, setNextButtonDisabled ] = useState(true)
    const [ serverError, setServerError ] = useState(error)
    const { CONFIRM_PASSWORD, PASSWORD } = formFields
    useEffect(() => {
        const { setFieldError, setFieldTouched, validateForm } = ref.current.getContext()
        setServerError(error)
        if(error){
            isServerErrorCleared.current = false
            setFieldError(PASSWORD, translate('oktaApiResponse.' + `${error}`))
            setFieldTouched(PASSWORD, true)
            setFieldValidity({
                confirmPassword: false,
                password: false
            })
            validateForm()
        }
    }, [ error, i18n.language, translate ]) // Re-run when error or language changes to update error message translation
   
    const [ pwdCriteria ] = useState({
        pwdCan: {
            criteria: [ {
                pass: false,
                regex: /.{8,}/,
                text: translate('pwFlow.characterLengthTextNew'),
                type: 'characterLengthTextNew'
            }, {
                pass: false,
                regex: /\d/,
                text: translate('pwFlow.charNumTextNew'),
                type: 'charNumTextNew'
            },
            {
                pass: false,
                regex: /(?=.*[a-z])(?=.*[A-Z])/,
                text: translate('pwFlow.lowerUpperCaseTextNew'),
                type: 'lowerUpperCaseTextNew'

            },
            {
                pass: false,
                regex: /[\W_]/,
                text: translate('pwFlow.specialCharTextNew'),
                type: 'specialCharTextNew'
            } ],
            title: translate('pwFlow.passwordContains')
        },
        pwdCanNot:{ 
            criteria: [ {
                pass: false,
                text: translate('pwFlow.partOfyourUsernameEmail'),
                type: 'partOfyourUsernameEmail'
            }, {

                pass: false,
                text: translate('pwFlow.repeatedChars'),
                type: 'repeatedChars'
            }, {
                pass: false,
                text: translate('pwFlow.sequence'),
                type: 'sequence'

            } ],
            title: ''
        }
    })
    const [ passwordCriteria, setPasswordCriteria ] = useState(pwdCriteria)


    useEffect(() => {
        setPasswordCriteria(currentState =>  {
            const { pwdCan, pwdCanNot } = currentState
            const newPwdCan = {
                criteria: pwdCan.criteria.map(cri => ({ ...cri, text: translate(`pwFlow.${cri.type}`) })),
                title: translate('pwFlow.passwordContains')
            }
            const newPwdCanNot = {
                criteria: pwdCanNot.criteria.map(cri => ({ ...cri, text: translate(`pwFlow.${cri.type}`) })),
                title: ' '
            }
            return {
                pwdCan: newPwdCan,
                pwdCanNot: newPwdCanNot
            }    
        })
        const { validateForm } = ref.current.getContext()
        validateForm()
    }, [ i18n.language ])

    const { firstName, lastName, login } = data


    const [ values, setValues ] = useState({
        confirmPassword: '',
        password: '',
        showConfirmPassword: false,
        showPassword: false,
    })

   
    const handlePasswordChange = e => {
        const { setFieldValue, setFieldError, validateForm, values } = ref.current.getContext()

        // Clear server error immediately when user starts typing
        if (serverError && !isServerErrorCleared.current) {
            isServerErrorCleared.current = true
            setServerError(null)
            setFieldError(PASSWORD, undefined)
        }

        values[PASSWORD] = e.target.value
        setFieldValue(PASSWORD, e.target.value)
        
        // Use refactored validation logic
        const { pwdCanCriteria, pwdCannotCriteria, allPwdCanPass, allPwdCannotPass } = validatePasswordCriteria(values[PASSWORD])
        
        setPasswordCriteria({
            pwdCan: { criteria: pwdCanCriteria, title: translate('pwFlow.passwordContains') },
            pwdCanNot: { criteria: pwdCannotCriteria, title: ' ' }
        })
        
        // Update field validity
        setFieldValidity(prev => ({
            ...prev,
            password: allPwdCanPass && allPwdCannotPass
        }))
        
        validateForm()
    }

    const handleClickShowPassword = useCallback(() => {
        setValues({ ...values, showPassword: !values.showPassword })
    }, [ values ])

    const handleClickShowConfirmPassword = useCallback(() => {
        setValues({ ...values, showConfirmPassword: !values.showConfirmPassword })
    }, [ values ])

    const handleConfirmPasswordChange = e => {
        const { setFieldValue,values, validateForm } = ref.current.getContext()
        values[CONFIRM_PASSWORD] = e.target.value
        setFieldValue(CONFIRM_PASSWORD, e.target.value)
        
        // Update field validity - check if passwords match and confirm password is not empty
        setFieldValidity(prev => ({
            ...prev,
            confirmPassword: values[CONFIRM_PASSWORD] !== '' && values[PASSWORD] === values[CONFIRM_PASSWORD]
        }))
        
        validateForm()
    }

    const hasSequentialChar = useCallback(hasSequentialChars, [])
    const hasIdenticalConsecutiveChar = useCallback(hasIdenticalConsecutiveChars, [])

    // Refactored password validation logic
    const validatePasswordCriteria = useCallback(password => {
        if (!password) {
            return {
                allPwdCannotPass: false,
                allPwdCanPass: false,
                pwdCanCriteria: pwdCriteria.pwdCan.criteria.map(cri => ({ ...cri, pass: false })),
                pwdCannotCriteria: pwdCriteria.pwdCanNot.criteria.map(cri => ({ ...cri, pass: false }))
            }
        }

        // Check pwdCan criteria (positive criteria)
        const pwdCanCriteria = pwdCriteria.pwdCan.criteria.map(cri => ({
            ...cri,
            pass: cri.regex.test(password)
        }))
        const allPwdCanPass = pwdCanCriteria.every(cri => cri.pass)

        // Check pwdCanNot criteria (negative criteria)
        const usernameCheck = !(login && passwordContainsUsername(password.toLowerCase(), login.toLowerCase()))
            && !(firstName && password.toLowerCase().includes(firstName.toLowerCase()))
            && !(lastName && password.toLowerCase().includes(lastName.toLowerCase()))
        const repeatedCheck = !hasIdenticalConsecutiveChar(password)
        const sequenceCheck = !hasSequentialChar(password)

        const pwdCannotCriteria = pwdCriteria.pwdCanNot.criteria.map(cri => {
            const { type } = cri
            let pass = false
            if (type === 'partOfyourUsernameEmail') {
                pass = usernameCheck
            } else if (type === 'repeatedChars') {
                pass = repeatedCheck
            } else if (type === 'sequence') {
                pass = sequenceCheck
            }
            return { ...cri, pass }
        })

        const allPwdCannotPass = usernameCheck && repeatedCheck && sequenceCheck

        return {
            allPwdCannotPass,
            allPwdCanPass,
            pwdCanCriteria,
            pwdCannotCriteria
        }
    }, [ firstName, hasIdenticalConsecutiveChar, hasSequentialChar, lastName, login, pwdCriteria.pwdCan.criteria, pwdCriteria.pwdCanNot.criteria ])

    const validate = values => {
        const errors = {}

        // Get touched state from form context
        const { touched } = ref.current ? ref.current.getContext() : { touched: {} }

        // Use refactored validation logic
        const { allPwdCanPass, allPwdCannotPass } = validatePasswordCriteria(values[PASSWORD])

        if (values[CONFIRM_PASSWORD] && (values[PASSWORD] !== values[CONFIRM_PASSWORD])) {
            errors[CONFIRM_PASSWORD] = translate('pwFlow.matchError')
        }

        for (const field of Object.values(formFields)) {
            if (!values[field]) {
                errors[field] = translate('required')
            }
        }
        setNextButtonDisabled(Object.keys(errors).length !== 0 || !allPwdCanPass || !allPwdCannotPass)

        if (serverError && !isServerErrorCleared.current) {
            errors[PASSWORD] = translate('oktaApiResponse.' + `${error}`)
        }
        const { values: formValues } = ref.current.getContext()
    
        
        // Update field validity - check if passwords match and confirm password is not empty
        setFieldValidity(prev => ({            
            confirmPassword: formValues[CONFIRM_PASSWORD] !== '' && formValues[PASSWORD] === formValues[CONFIRM_PASSWORD],
            password: allPwdCanPass && allPwdCannotPass
        }))
        
        return errors
    }

    const stepTitle = translate('registrationWelcome.card2Title')
    const stepVerbiage = translate('registrationFlow.setPasswordVerbiage')

    // Password fields configuration
    const passwordFields = [
        {
            ariaLabel: 'toggle current password visibility',
            autoFocus: error ? false :true,
            field: PASSWORD,
            label: translate('pwFlow.password'),
            onChange: handlePasswordChange,
            showPassword: values.showPassword,
            toggleVisibility: handleClickShowPassword
        },
        {
            ariaLabel: translate('pwFlow.toggleConfirmPw'),
            autoFocus: false,
            field: CONFIRM_PASSWORD,
            label: translate('pwFlow.confirmPw'),
            onChange: handleConfirmPasswordChange,
            placeholder: translate('pwFlow.confirmPw'),
            showPassword: values.showConfirmPassword,
            toggleVisibility: handleClickShowConfirmPassword
        }
    ]

    const mainContent = useMemo(() => (
        <>
            <div className={classes.introText}>
                <p className={classes.subTitle}>{stepVerbiage}</p>
            </div>

            <Form {...{
                enableReinitialize: true,
                initialValues: initialValues,
                onSubmit: onNext,
                ref,
                validate,
                validateOnBlur: true
            }}>
                <div className={classes.fieldsContainer}>
                    <Grid className={classes.passwordContainer} container spacing={2}>
                        {passwordFields.map( fieldConfig => (
                            <Grid item key={fieldConfig.field} md={12} xs={12}>
                                <TextField
                                    autoFocus={fieldConfig.autoFocus}
                                    className={`${classes.inputField} ${fieldValidity[fieldConfig.field] ? classes.validField : ''}`}
                                    field={fieldConfig.field}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={fieldConfig.ariaLabel}
                                                    onClick={fieldConfig.toggleVisibility}
                                                >
                                                    {fieldConfig.showPassword
                                                        ? <Visibility className={classes.visibilityIconButton} />
                                                        : <VisibilityOff className={classes.visibilityIconButton} />
                                                    }
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        onChange: fieldConfig.onChange
                                    }}
                                    label={fieldConfig.label}
                                    placeholder={fieldConfig.placeholder}
                                    type={fieldConfig.showPassword ? 'text' : 'password'}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </div>
            </Form>
            <Container className={classes.criteriaContainer}>
                {Object.values(passwordCriteria).map((criteria,index) => {
                    return <Container key={ `criteria-${index}`} className={index == 1 && classes.secondCriteria}>
                        <span className={classes.criteriaMainTitle}>{criteria.title || ' '}</span>
                        <List className={classes.passwordCriteriaList}>
                            {criteria.criteria.map((value, subIndex) => (
                                <ListItem className={classes.listItem} key={`criteria-${index}-item-${subIndex}`}>
                                    <ListItemIcon className={`${classes.listItemIcon} ${index == 0 && classes.positiveCriteria}`}>
                                        <CheckCircleIcon className={`${classes.passwordError} ${value.pass && classes.passwordSuccess}`} />
                                    </ListItemIcon>
                                    <ListItemText className={`${classes.criteriaTitle} ${value.pass && classes.passwordSuccess}`} primary={translate(`pwFlow.${value.type}`)} />
                                </ListItem>
                            ))}
                        </List>
                    </Container>
                })}
                <p className={`${classes.subTitle} ${classes.supportText}`}>{translate('pwFlow.otherCriteria')}</p>
            </Container>
            <div className={classes.helpText}>
                <p className={classes.subTitle}>{translate('registrationFlow.helpText')}</p>
            </div>
        </>
    ), [ classes.criteriaContainer, classes.criteriaMainTitle, classes.criteriaTitle, classes.fieldsContainer, classes.helpText, classes.inputField, classes.introText, classes.listItem, classes.listItemIcon, classes.passwordContainer, classes.passwordCriteriaList, classes.passwordError, classes.passwordSuccess, classes.positiveCriteria, classes.subTitle, classes.supportText, classes.validField, classes.visibilityIconButton, fieldValidity, handleClickShowConfirmPassword, handleClickShowPassword, handleConfirmPasswordChange, handlePasswordChange, passwordCriteria, passwordFields, stepVerbiage, translate, values.showConfirmPassword, values.showPassword ])

    // Footer buttons configuration
    const footerButtons = [
        {
            className: `${classes.nextButton} ${nextButtonDisabled && classes.nextButtonDisabled}`,
            disabled: nextButtonDisabled,
            label: translate('registrationAnalytics.submit'),
            onClick: useCallback(() => {
                const { values } = ref.current.getContext()
                onNext(values)
            })
        }
    ]

    const footerContent = useMemo(() => (
        <Grid className={classes.buttonContainer}>
            {footerButtons.map((button, index) => (
                <Button
                    key={`footer-button-${index}`}
                    className={button.className}
                    color='primary'
                    disabled={button.disabled}
                    onClick={button.onClick}
                >
                    {button.label}
                </Button>
            ))}
        </Grid>
    ), [ classes.buttonContainer, footerButtons ])
    return (
        <RegistrationContainer {...{ footerActions : footerContent, mainContent,  title: stepTitle, verbiage: stepVerbiage }}>
           
        </RegistrationContainer>
    )
}

Component.displayName = 'UserPassword'

Component.defaultProps = {
    data: {
        login: ''
    }
}


Component.propTypes = {
    classes: PropTypes.shape({
        actionRow: PropTypes.object,
        button: PropTypes.object,
        buttonContainer: PropTypes.object,
        contentDivider: PropTypes.object,
        fieldsContainer: PropTypes.object,
        passwordContainer: PropTypes.object,
        passwordError: PropTypes.object,
        passwordSuccess: PropTypes.object

    }),
    data: PropTypes.object.isRequired,
    error: PropTypes.object,
    onNext: PropTypes.func.isRequired
}

export default withStyles(Component, styles)
